import React from "react";
import { Box, Button, Heading, Text, VStack } from "@chakra-ui/react";
import { reportError } from "../lib/reportError";

/**
 * Catches render-phase errors in the patient app component tree and reports
 * them to the in-house collector. Renders a minimal fallback with a reload
 * button so a single broken component doesn't white-screen the patient.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error, info) {
    reportError(error, {
      extra: {
        componentStack:
          info && info.componentStack
            ? info.componentStack.slice(0, 2000)
            : undefined,
      },
    });
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
        bg="gray.50"
      >
        <VStack
          spacing={4}
          maxW="lg"
          p={8}
          bg="white"
          borderRadius="md"
          boxShadow="md"
          textAlign="center"
        >
          <Heading size="md">Something went wrong</Heading>
          <Text color="gray.600">
            An unexpected error occurred. Our team has been notified. You can
            reload the page to try again.
          </Text>
          <Button colorScheme="blue" onClick={this.handleReload}>
            Reload
          </Button>
        </VStack>
      </Box>
    );
  }
}
